import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ResultsTable from '../components/ResultsTable';
import PersonalizedOpener from '../components/PersonalizedOpener';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBarChart2, FiTrendingUp, FiFilter, FiSearch, FiDownload } = FiIcons;

const Analysis = ({ results, verifiedLeads = [] }) => {
  const [sortBy, setSortBy] = useState('fitScore');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedWebsite, setSelectedWebsite] = useState(null);

  // Combine verified leads with existing results if they're not already included
  const combinedResults = [...results];
  
  // Add verified leads that aren't already in the results
  verifiedLeads.forEach(lead => {
    if (!combinedResults.some(result => result.email === lead.email)) {
      combinedResults.push({
        ...lead,
        // Set default values for analysis fields if they don't exist
        fitScore: lead.fitScore || 0,
        analyzedAt: lead.analyzedAt || new Date().toISOString(),
        emailVerified: true
      });
    }
  });
  
  // Filter and sort results
  const filteredResults = combinedResults
    .filter(result => {
      // Apply search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const companyMatch = (result.company || '').toLowerCase().includes(searchLower);
        const urlMatch = (result.url || '').toLowerCase().includes(searchLower);
        if (!companyMatch && !urlMatch) return false;
      }

      // Apply score filter
      if (selectedFilter === 'high') return result.fitScore >= 7;
      if (selectedFilter === 'medium') return result.fitScore >= 5 && result.fitScore < 7;
      if (selectedFilter === 'low') return result.fitScore < 5;
      return true;
    })
    .sort((a, b) => {
      // Apply sorting
      if (sortBy === 'date') {
        const dateA = new Date(a.analyzedAt || 0).getTime();
        const dateB = new Date(b.analyzedAt || 0).getTime();
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      } else if (sortBy === 'company') {
        const companyA = (a.company || '').toLowerCase();
        const companyB = (b.company || '').toLowerCase();
        return sortDirection === 'asc' ? companyA.localeCompare(companyB) : companyB.localeCompare(companyA);
      } else {
        // Default sort by fitScore
        return sortDirection === 'asc' ? (a.fitScore || 0) - (b.fitScore || 0) : (b.fitScore || 0) - (a.fitScore || 0);
      }
    });

  // Toggle sort direction
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  const exportToCsv = () => {
    if (filteredResults.length === 0) return;

    // Create CSV content
    const headers = ['Company', 'Website', 'Fit Score', 'Analysis Date', 'Opportunities', 'Personalized Opener'];
    const csvContent = [
      headers.join(','),
      ...filteredResults.map(item => [
        `"${item.company || ''}"`,
        `"${item.url || ''}"`,
        item.fitScore || 0,
        item.analyzedAt ? new Date(item.analyzedAt).toLocaleDateString() : '',
        `"${(item.opportunities || '').replace(/"/g, '""')}"`,
        `"${(item.personalizedOpener || '').replace(/"/g, '""')}"`,
      ].join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `website-analysis-${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Group results by score category
  const highScoreResults = results.filter(r => r.fitScore >= 7).length;
  const mediumScoreResults = results.filter(r => r.fitScore >= 5 && r.fitScore < 7).length;
  const lowScoreResults = results.filter(r => r.fitScore < 5).length;

  // Calculate average score
  const averageScore = results.length > 0 ? (results.reduce((sum, item) => sum + (item.fitScore || 0), 0) / results.length).toFixed(1) : 0;

  // Handle website selection
  const handleWebsiteSelect = (website) => {
    setSelectedWebsite(website);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="analysis-page"
    >
      <div className="page-header">
        <h1 className="page-title">Website Analysis</h1>
      </div>

      <div className="score-summary">
        <div className="summary-card">
          <div className="summary-icon">
            <SafeIcon icon={FiBarChart2} />
          </div>
          <div className="summary-content">
            <div className="summary-value">{averageScore}</div>
            <div className="summary-label">Average Score</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon high">
            <SafeIcon icon={FiTrendingUp} />
          </div>
          <div className="summary-content">
            <div className="summary-value">{highScoreResults}</div>
            <div className="summary-label">High Potential</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon medium">
            <SafeIcon icon={FiTrendingUp} />
          </div>
          <div className="summary-content">
            <div className="summary-value">{mediumScoreResults}</div>
            <div className="summary-label">Medium Potential</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon low">
            <SafeIcon icon={FiTrendingUp} />
          </div>
          <div className="summary-content">
            <div className="summary-value">{lowScoreResults}</div>
            <div className="summary-label">Low Potential</div>
          </div>
        </div>
      </div>

      <div className="analysis-layout">
        <div className="analysis-main">
          <div className="analysis-controls">
            <div className="search-box">
              <SafeIcon icon={FiSearch} className="search-icon" />
              <input
                type="text"
                placeholder="Search websites..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="filter-controls">
              <div className="filter-dropdown">
                <SafeIcon icon={FiFilter} className="filter-icon" />
                <select
                  value={selectedFilter}
                  onChange={(e) => setSelectedFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Scores</option>
                  <option value="high">High Score (7+)</option>
                  <option value="medium">Medium Score (5-6)</option>
                  <option value="low">Low Score (1-4)</option>
                </select>
              </div>
              <button className="export-button" onClick={exportToCsv}>
                <SafeIcon icon={FiDownload} />
                <span>Export</span>
              </button>
            </div>
          </div>

          <div className="analysis-table-container">
            {filteredResults.length > 0 ? (
              <table className="analysis-table">
                <thead>
                  <tr>
                    <th>Website</th>
                    <th
                      onClick={() => handleSort('date')}
                      className="sortable-header"
                    >
                      Analysis Date
                      {sortBy === 'date' && (
                        <span className="sort-indicator">
                          {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </th>
                    <th
                      onClick={() => handleSort('fitScore')}
                      className="sortable-header"
                    >
                      Fit Score
                      {sortBy === 'fitScore' && (
                        <span className="sort-indicator">
                          {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </th>
                    <th>Qualification</th>
                    <th>Key Opportunities</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.map((result, index) => {
                    // Generate an initial for the avatar
                    const initial = (result.company || 'A')[0].toUpperCase();

                    // Determine status class
                    const getStatusClass = (score) => {
                      if (score >= 7) return "status-confirmed";
                      if (score >= 5) return "status-pending";
                      return "status-low";
                    };

                    return (
                      <tr 
                        key={index}
                        onClick={() => handleWebsiteSelect(result)}
                        className={selectedWebsite?.url === result.url ? 'selected-row' : ''}
                      >
                        <td>
                          <div className="website-cell">
                            <div
                              className="user-avatar-small"
                              style={{
                                backgroundColor: ['#818cf8', '#f472b6', '#fb923c'][index % 3]
                              }}
                            >
                              {initial}
                            </div>
                            <div className="website-info">
                              <div className="website-company">{result.company || 'Unknown'}</div>
                              <div className="website-url" title={result.url}>
                                {result.url ? new URL(result.url).hostname : 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {result.analyzedAt ? new Date(result.analyzedAt).toLocaleDateString() : 'N/A'}
                        </td>
                        <td>
                          <span className={`score-badge ${getStatusClass(result.fitScore)}`}>
                            {result.fitScore || 'N/A'}
                          </span>
                        </td>
                        <td>
                          <div className="qualification-status">
                            {result.qualificationSuccess ? (
                              <div className="qualification-success">
                                <span className="qualification-badge success">✓ Qualified</span>
                                {result.confidence && (
                                  <span className="confidence-text">
                                    {(() => {
                                      const confidenceMap = {
                                        'high': 90,
                                        'medium': 70,
                                        'low': 50
                                      };
                                      const numericConfidence = typeof result.confidence === 'number' 
                                        ? result.confidence * 100 
                                        : confidenceMap[result.confidence] || 70;
                                      return Math.round(numericConfidence);
                                    })()}%
                                  </span>
                                )}
                              </div>
                            ) : result.qualificationSuccess === false ? (
                              <span className="qualification-badge error">✗ Failed</span>
                            ) : (
                              <span className="qualification-badge pending">- Pending</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="opportunities-text">
                            {result.opportunities
                              ? result.opportunities.length > 100
                                ? `${result.opportunities.substring(0, 100)}...`
                                : result.opportunities
                              : 'No opportunities found'}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="empty-analysis">
                <p>No analysis results match your search criteria</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="analysis-sidebar">
          <PersonalizedOpener website={selectedWebsite} />
          
          {selectedWebsite && (
            <div className="opportunities-card">
              <div className="card-header">
                <h3>Redesign Opportunities</h3>
              </div>
              <div className="card-content">
                <p className="opportunities-text">
                  {selectedWebsite.opportunities || 'No specific opportunities identified'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .analysis-page {
          padding: 1.5rem;
        }
        
        .page-header {
          margin-bottom: 1.5rem;
        }
        
        .page-title {
          font-size: 1.5rem;
          font-weight: 600;
        }
        
        .score-summary {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        
        .summary-card {
          background-color: white;
          border-radius: 8px;
          padding: 1rem;
          display: flex;
          align-items: center;
          box-shadow: var(--shadow-sm);
        }
        
        .summary-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
          background-color: #eff6ff;
          color: var(--blue-color);
        }
        
        .summary-icon.high {
          background-color: #ecfdf5;
          color: var(--green-color);
        }
        
        .summary-icon.medium {
          background-color: #fff7ed;
          color: var(--orange-color);
        }
        
        .summary-icon.low {
          background-color: #fef2f2;
          color: #f87171;
        }
        
        .summary-content {
          flex: 1;
        }
        
        .summary-value {
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1.2;
        }
        
        .summary-label {
          font-size: 0.875rem;
          color: var(--text-light);
          margin-top: 0.125rem;
        }
        
        .analysis-layout {
          display: grid;
          grid-template-columns: 1fr 350px;
          gap: 1.5rem;
        }
        
        .analysis-controls {
          display: flex;
          justify-content: space-between;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 1rem;
        }
        
        .search-box {
          position: relative;
          flex: 1;
          min-width: 200px;
        }
        
        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-light);
        }
        
        .search-input {
          width: 100%;
          padding: 0.5rem 0.5rem 0.5rem 2.25rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.875rem;
        }
        
        .filter-controls {
          display: flex;
          gap: 0.75rem;
          align-items: center;
        }
        
        .filter-dropdown {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .filter-icon {
          position: absolute;
          left: 0.75rem;
          color: var(--text-light);
          pointer-events: none;
        }
        
        .filter-select {
          padding: 0.5rem 0.5rem 0.5rem 2.25rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.875rem;
          background-color: white;
          min-width: 150px;
        }
        
        .export-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
        }
        
        .export-button:hover {
          background-color: #f9fafb;
        }
        
        .analysis-table-container {
          background-color: white;
          border-radius: 8px;
          box-shadow: var(--shadow-sm);
          overflow: hidden;
          margin-bottom: 1.5rem;
        }
        
        .analysis-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }
        
        .analysis-table th {
          text-align: left;
          padding: 0.75rem 1rem;
          color: var(--text-light);
          font-weight: 500;
          border-bottom: 1px solid #e5e7eb;
          background-color: #f9fafb;
        }
        
        .sortable-header {
          cursor: pointer;
          user-select: none;
        }
        
        .sort-indicator {
          color: var(--primary-color);
          font-weight: bold;
        }
        
        .analysis-table td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #e5e7eb;
          vertical-align: top;
        }
        
        .analysis-table tr {
          cursor: pointer;
        }
        
        .analysis-table tr:hover {
          background-color: #f9fafb;
        }
        
        .selected-row {
          background-color: #f5f3ff !important;
          border-left: 3px solid var(--primary-color);
        }
        
        .website-cell {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .website-info {
          display: flex;
          flex-direction: column;
        }
        
        .website-company {
          font-weight: 500;
        }
        
        .website-url {
          font-size: 0.75rem;
          color: var(--text-light);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 180px;
        }
        
        .score-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          font-weight: 600;
        }
        
        .status-confirmed {
          background-color: #ecfdf5;
          color: #065f46;
        }
        
        .status-pending {
          background-color: #fff7ed;
          color: #9a3412;
        }
        
        .status-low {
          background-color: #f3f4f6;
          color: #4b5563;
        }
        
        .opportunities-text {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          color: var(--text-light);
        }
        
        .empty-analysis {
          padding: 3rem;
          text-align: center;
          color: var(--text-light);
        }
        
        .analysis-sidebar {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        
        .opportunities-card {
          background-color: white;
          border-radius: var(--small-radius);
          box-shadow: var(--shadow-sm);
        }
        
        .card-header {
          padding: 1rem;
          border-bottom: 1px solid #f3f4f6;
        }
        
        .card-header h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
        }
        
        .card-content {
          padding: 1rem;
        }
        
        .qualification-status {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        
        .qualification-success {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        
        .qualification-badge {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          border-radius: 12px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          width: fit-content;
        }
        
        .qualification-badge.success {
          background-color: #ecfdf5;
          color: #065f46;
        }
        
        .qualification-badge.error {
          background-color: #fef2f2;
          color: #dc2626;
        }
        
        .qualification-badge.pending {
          background-color: #f3f4f6;
          color: #6b7280;
        }
        
        .confidence-text {
          font-size: 0.625rem;
          color: #6b7280;
          font-weight: 400;
        }
        
        @media (max-width: 1024px) {
          .analysis-layout {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default Analysis;